import { memo } from 'react';
import { Typography, Tooltip, message } from 'antd';

import styles from './report.module.css';
import ReactMarkdown from 'react-markdown';
import { Bar, Column, RadialBar, RingProgress } from '@ant-design/charts';
import { Colors } from '../../core/Theme';
import { formatPercentage } from '../../../components/Percent';
import { useQuery } from '@tanstack/react-query';
import { ReportProps } from './Report';
import SurveysService from '../SurveysService';

type DashboardProps = Omit<ReportProps, 'questions'> & {
  dashboard: any;
  index: number;
  loading: boolean;
  alternate_colors?: boolean;
};

const getStyleForScore = (score: number) => {
  if (score < 0) return { color: Colors.error };
  else if (score >= 0 && score < 50) return { color: Colors.warning };
  else return { color: Colors.success };
};

const Dashboard = ({
  dashboard,
  index,
  alternate_colors,
  filters,
  surveyId,
  client,
}: DashboardProps) => {
  const queryData = async () => {
    let data = await SurveysService.getDashboardData(
      client,
      surveyId,
      dashboard.id,
      filters
    );
    return data;
  };

  const { isLoading, data: dashboardData } = useQuery(
    ['survey_dashboard', surveyId, dashboard.id, filters],
    queryData,
    {
      onError: (err) => {
        console.error(err);
        message.error((err as any).message);
      },
    }
  );

  const title = (
    <Typography.Title level={4}>
      {
        <ReactMarkdown
          components={{
            a: (props) => (
              <a href={props.href} rel='noopener noreferrer' target='_blank'>
                {props.children}
              </a>
            ),
          }}
        >
          {dashboard.title}
        </ReactMarkdown>
      }
    </Typography.Title>
  );
  const data = dashboardData?.results || [];

  return (
    <div className={styles.dash}>
      {dashboard.description ? (
        <Tooltip title={dashboard.description}>{title}</Tooltip>
      ) : (
        title
      )}
      {dashboard.type === 'Bar' && (
        <Bar
          data={data.sort((a: any, b: any) => {
            let ord =
              dashboard.group === 'nps'
                ? ['Promotores', 'Neutros', 'Detractores']
                : ['Satisfechos', 'Neutros', 'Insatisfechos'];
            if (a.order === b.order)
              return ord.indexOf(a.type) - ord.indexOf(b.type);
            return a.order - b.order;
          })}
          loading={isLoading}
          maxBarWidth={30}
          yField='text'
          xField='average'
          autoFit={true}
          isStack={
            dashboard.group === 'tags' ||
            dashboard.group === 'si' ||
            dashboard.group === 'nps'
          }
          seriesField={
            dashboard.group === 'tags' || dashboard.group ? 'type' : 'order'
          }
          xAxis={{ grid: null, label: null }}
          yAxis={{
            grid: null,
            line: null,
            label: {
              autoEllipsis: true,
              style: {
                fontSize: 10,
                fontFamily: '"Montserrat", sans-serif',
                fill: Colors.black,
                fontWeight: 'bold',
              },
            },
          }}
          legend={false}
          tooltip={
            data.length > 30
              ? {
                  title: (_title, datum) =>
                    `Cant. respuestas: ${formatPercentage(datum.total, 1)}`,
                }
              : false
          }
          label={{
            style: {
              fill: Colors.black,
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 'bold',
              fontSize: 11,
              textAlign: 'center',
            },
            layout: [{ type: 'adjust-color' }],
            position: 'middle',
            offsetX: 0,
            content: (data) => formatPercentage(data.average, 1),
          }}
          color={
            dashboard.group === 'tags' || dashboard.group === 'si'
              ? (datum) =>
                  datum.type === 'Insatisfechos'
                    ? alternate_colors
                      ? Colors.error
                      : Colors.charts.gray
                    : datum.type === 'Satisfechos'
                    ? alternate_colors
                      ? Colors.success
                      : Colors.charts.green
                    : alternate_colors
                    ? Colors.warning
                    : Colors.charts.greenish
              : dashboard.group === 'nps'
              ? (datum) =>
                  datum.type === 'Detractores'
                    ? Colors.error
                    : datum.type === 'Promotores'
                    ? Colors.success
                    : Colors.warning
              : (datum) =>
                  Colors.charts[
                    Object.keys(Colors.charts)[(datum.order - 1) % 5]
                  ]
          }
        />
      )}
      {dashboard.type === 'Column' && (
        <Column
          data={data.sort((a: any, b: any) => {
            let ord =
              dashboard.group === 'nps'
                ? ['Promotores', 'Neutros', 'Detractores']
                : ['Satisfechos', 'Neutros', 'Insatisfechos'];
            if (a.order === b.order)
              return ord.indexOf(a.type) - ord.indexOf(b.type);
            return a.order - b.order;
          })}
          loading={isLoading}
          maxColumnWidth={70}
          xField='text'
          yField='average'
          isStack={
            dashboard.group === 'tags' ||
            dashboard.group === 'si' ||
            dashboard.group === 'nps'
          }
          seriesField={
            dashboard.group === 'tags' || dashboard.group ? 'type' : 'order'
          }
          height={300}
          yAxis={{ grid: null, label: null }}
          xAxis={{
            grid: null,
            line: null,
            verticalLimitLength: 120,
            label: {
              autoEllipsis: true,
              autoHide: false,
              autoRotate: dashboard.group === 'tags' ? false : true,
              style: {
                fontSize: 10,
                fontFamily: '"Montserrat", sans-serif',
                fill: Colors.black,
                fontWeight: 'bold',
              },
            },
          }}
          tooltip={
            data.length > 30
              ? {
                  title: (_title, datum) => `${datum.text}`,
                  formatter: (datum) => ({
                    name: `${datum.type}`,
                    value: `${formatPercentage(datum.average, 1)}`,
                  }),
                }
              : false
          }
          legend={
            dashboard.survey_tag_id === null
              ? {
                  offsetX: data.length === 3 ? -30 : 0,
                  position: 'right',
                }
              : false
          }
          label={{
            layout: [{ type: 'adjust-color' }],
            style: {
              fill: Colors.black,
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 'bold',
              fontSize: 10,
              textAlign: 'center',
            },
            position:
              dashboard.group === 'tags' ||
              dashboard.group === 'si' ||
              dashboard.group === 'nps'
                ? 'middle'
                : 'top',
            offsetY:
              dashboard.group === 'tags' ||
              dashboard.group === 'si' ||
              dashboard.group === 'nps'
                ? 0
                : 10,
            content: (d) =>
              data.length > 30 || d.average < 0.05
                ? ''
                : formatPercentage(d.average, 1),
          }}
          color={
            dashboard.group === 'tags' || dashboard.group === 'si'
              ? (datum) =>
                  datum.type === 'Insatisfechos'
                    ? alternate_colors
                      ? Colors.error
                      : Colors.charts.gray
                    : datum.type === 'Satisfechos'
                    ? alternate_colors
                      ? Colors.success
                      : Colors.charts.green
                    : alternate_colors
                    ? Colors.warning
                    : Colors.charts.greenish
              : dashboard.group === 'nps'
              ? (datum) =>
                  datum.type === 'Detractores'
                    ? Colors.error
                    : datum.type === 'Promotores'
                    ? Colors.success
                    : Colors.warning
              : (datum) =>
                  Colors.charts[
                    Object.keys(Colors.charts)[(datum.order - 1) % 5]
                  ]
          }
        />
      )}
      {dashboard.type === 'Ring' && (
        <RingProgress
          loading={isLoading}
          autoFit={true}
          innerRadius={0.8}
          percent={dashboard.results[0]?.average || 0}
          color={[Colors.charts.green, Colors.gray]}
          statistic={{
            content: {
              style: {
                fontSize: '1.5em',
                fontWeight: 'bold',
                color: Colors.black,
              },
              formatter: (dat) =>
                dat ? formatPercentage(dat.percent, 1) : '-',
            },
          }}
        />
      )}
      {dashboard.type === 'NPS' && (
        <div className={styles.nps}>
          <div className={styles.npsScore}>
            <span>NPS: </span>
            <span
              style={getStyleForScore(
                Math.round(
                  ((data.find((d: any) => d.type === 'Promotores')?.value -
                    data.find((d: any) => d.type === 'Detractores')?.value) /
                    data.reduce(
                      (prev: number, curr: any) => prev + curr.value,
                      0
                    )) *
                    100
                )
              )}
            >
              {Math.round(
                ((data.find((d: any) => d.type === 'Promotores')?.value -
                  data.find((d: any) => d.type === 'Detractores')?.value) /
                  data.reduce(
                    (prev: number, curr: any) => prev + curr.value,
                    0
                  )) *
                  100
              )}
            </span>
          </div>
          <RadialBar
            data={data}
            autoFit
            loading={isLoading}
            xField='type'
            yField='value'
            radius={0.8}
            height={250}
            innerRadius={0.3}
            interactions={[]}
            barBackground={{ style: { fill: 'rgba(255,255,255,0.45)' } }}
            barStyle={{ lineCap: 'round' }}
            colorField='type'
            minBarWidth={27}
            tooltip={false}
            xAxis={{
              grid: null,
              label: {
                style: {
                  fill: Colors.black,
                  fontFamily: '"Montserrat", sans-serif',
                  fontWeight: 'bold',
                  fontSize: 13,
                  textAlign: 'right',
                },
                offset: 20,
                formatter: (_text, _item, number) => {
                  return formatPercentage(
                    data[number].value /
                      (data.reduce(
                        (prev: number, next: any) => prev + next.value,
                        0
                      ) || 1),
                    1
                  );
                },
              },
            }}
            color={[Colors.error, Colors.warning, Colors.success]}
          />
        </div>
      )}
    </div>
  );
};

export default memo(Dashboard);
